import { PropsWithChildren, useCallback, useRef, useState } from "react";
import { Text, TextProps } from "@radix-ui/themes";
import Tooltip from "./Tooltip";

type TextWithTooltipProps = TextProps & {
  showTooltip?: boolean;
  tooltipContent?: React.ReactNode;
  alignOffset?: number;
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  collisionPadding?: number;
};

export function TextWithTooltip({
  children,
  showTooltip = true,
  tooltipContent,
  truncate,
  style,
  alignOffset,
  side,
  sideOffset,
  collisionPadding,
  ...textProps
}: PropsWithChildren<TextWithTooltipProps>) {
  const [needsTooltip, setNeedsTooltip] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = useCallback(() => {
    if (tooltipContent) {
      setNeedsTooltip(true);
      return;
    }
    const el = textRef.current;
    if (el && el.scrollWidth > el.clientWidth) {
      setNeedsTooltip(true);
    }
  }, [tooltipContent]);

  const handleMouseLeave = useCallback(() => {
    setNeedsTooltip(false);
  }, []);

  if (!showTooltip) {
    return <Text {...textProps}>{children}</Text>;
  }

  if (!needsTooltip) {
    return (
      <Text
        {...textProps}
        ref={textRef}
        truncate={truncate}
        style={style}
        onMouseEnter={handleMouseEnter}
      >
        {children}
      </Text>
    );
  }

  return (
    <Tooltip>
      <Tooltip.Trigger>
        <Text
          {...textProps}
          ref={textRef}
          truncate={truncate}
          style={style}
          onMouseLeave={handleMouseLeave}
        >
          {children}
        </Text>
      </Tooltip.Trigger>

      <Tooltip.Content
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
      >
        <Text {...textProps} className="max-w-[600px]">
          {tooltipContent ?? children}
        </Text>
      </Tooltip.Content>
    </Tooltip>
  );
}
